import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';

/** Count numeric digits in tax id (FEIN-style entry may include dashes/spaces). */
export function countIdDigits(v: unknown): number {
	const s = v == null ? '' : String(v);
	return (s.match(/\d/g) || []).length;
}

/**
 * When set in assets config (AA clients), ship-to tax id requires this many digits
 * and tax-authority narrowing runs only after this many digits are entered.
 */
export function taxRefMinDigitsFromConfig(config: any): number | null {
	const raw = config?.customerShiptoTaxRef?.minDigits ?? config?.customerShiptoTaxRefMinDigits;
	const n = Number(raw);
	return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/**
 * Digit counts that are accepted as a complete tax id even though they fall short
 * of minDigits (e.g. some AA exempt certificates are a plain 6-digit number).
 */
export function taxRefAltDigitsFromConfig(config: any): number[] {
	const raw = config?.customerShiptoTaxRef?.altDigits;
	const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
	return list
		.map((v: unknown) => Number(v))
		.filter((n: number) => Number.isFinite(n) && n > 0)
		.map((n: number) => Math.floor(n));
}

/** Whether the digit count of a tax id satisfies minDigits or one of altDigits. */
function digitsSatisfy(actual: number, minDigits: number, altDigits: number[]): boolean {
	return actual >= minDigits || altDigits.includes(actual);
}

/** Whether to run tax-authority filter logic for the current tax ref value. */
export function shouldApplyTaxRefFilter(value: unknown, minDigits: number | null, altDigits: number[] = []): boolean {
	const s = value == null ? '' : String(value).trim();
	if (!s) {
		return false;
	}
	if (minDigits != null) {
		return digitsSatisfy(countIdDigits(value), minDigits, altDigits);
	}
	return true;
}

export function taxRefValidatorsForEdit(minDigits: number | null, altDigits: number[] = []): any[] {
	if (minDigits != null) {
		return [
			(control: AbstractControl): ValidationErrors | null =>
				digitsSatisfy(countIdDigits(control.value), minDigits, altDigits)
					? null
					: { taxRefMinDigits: { required: minDigits, altDigits, actual: countIdDigits(control.value) } },
			Validators.maxLength(20),
		];
	}
	return [Validators.minLength(3), Validators.maxLength(20)];
}

export function taxRefValidatorsForAdd(minDigits: number | null, altDigits: number[] = []): any[] {
	if (minDigits != null) {
		return [
			(control: AbstractControl): ValidationErrors | null =>
				digitsSatisfy(countIdDigits(control.value), minDigits, altDigits)
					? null
					: { taxRefMinDigits: { required: minDigits, altDigits, actual: countIdDigits(control.value) } },
			Validators.maxLength(20),
		];
	}
	return [Validators.minLength(9), Validators.maxLength(20)];
}

/** Validators for taxable customers (tax auth starts with '1'). No minimum length is required. */
export function taxRefValidatorsForTaxable(): any[] {
	return [Validators.maxLength(20)];
}
